<template>
  <div class="relative w-full">
    <div class="flex justify-between items-center w-full px-2">
      <span class="text-xs font-semibold">Extra Info</span>
      <div class="flex gap-1">
        <!-- Sort button -->
        <button
          @click="toggleSortMenu"
          class="px-1.5 py-0.5 rounded text-xs transition-all duration-200 hover:bg-gray-100"
          :class="(showSortMenu || currentSort) ? 'bg-primary-500 text-white hover:bg-primary-500' : ''"
          title="Sort extra info data"
        >
          <span v-if="!currentSort">⇅</span>
          <span v-else>{{ currentSort.direction === 'asc' ? '↑' : '↓' }}</span>
        </button>

        <!-- Filter button -->
        <button
          @click="toggleFilterMenu"
          class="px-1.5 py-0.5 rounded text-xs transition-all duration-200 hover:bg-gray-100"
          :class="(showFilterMenu || hasActiveFilters) ? 'bg-primary-500 text-white hover:bg-primary-500' : ''"
          title="Filter extra info data"
        >
          🔍
        </button>
      </div>
    </div>

    <!-- Sort dropdown -->
    <div
      v-if="showSortMenu"
      class="fixed bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-fadeIn"
      :style="dropdownStyle"
      ref="sortMenu"
    >
      <div class="px-3 py-2 text-xs font-semibold text-gray-600 uppercase bg-gray-50 border-b border-gray-200">
        Sort by:
      </div>
      <div class="py-1">
        <button
          v-for="option in sortOptions"
          :key="option.field"
          @click="applySort(option)"
          class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors duration-150 flex justify-between items-center"
          :class="currentSort?.field === option.field ? 'bg-blue-50 text-blue-700' : ''"
        >
          {{ option.label }}
          <span v-if="currentSort?.field === option.field" class="text-xs ml-2">
            {{ currentSort.direction === 'asc' ? '↑' : '↓' }}
          </span>
        </button>
      </div>
      <div class="border-t border-gray-200">
        <button
          @click="clearSort"
          class="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
        >
          Clear Sort
        </button>
      </div>
    </div>

    <!-- Filter dropdown -->
    <div
      v-if="showFilterMenu"
      class="fixed bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-fadeIn"
      :style="dropdownStyle"
      ref="filterMenu"
    >
      <div class="px-3 py-2 text-xs font-semibold text-gray-600 uppercase bg-gray-50 border-b border-gray-200">
        Filter by:
      </div>

      <div class="p-3 space-y-2">
        <!-- Contracted Measure Name Filter - Multiple Select Dropdown -->
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Measure Name</label>
          <StandardMultipleDropdown
            v-model="filters.contractedMeasureName"
            :options="measureNameOptions"
            placeholder="Select Measure Names..."
            :teleportToBody="true"
            @change="applyFilters"
          />
        </div>

        <!-- Pharmacy Filter -->
        <input
          v-model="filters.pharmacy"
          @input="applyFilters"
          placeholder="Pharmacy"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
        >
      </div>

      <div class="border-t border-gray-200">
        <button
          @click="clearFilters"
          class="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
        >
          Clear All Filters
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import StandardMultipleDropdown from '@/components/widgetcomponents/Dropdown_Selector/Standard_Multiple_Dropdown.vue';

export default {
  name: 'ExtraInfoHeaderComponent',
  components: {
    StandardMultipleDropdown
  },
  props: ['params'],
  data() {
    return {
      showSortMenu: false,
      showFilterMenu: false,
      currentSort: null,
      filters: {
        contractedMeasureName: [],
        pharmacy: ''
      },
      sortOptions: [
        { field: 'metadata.next_appointment_date', label: 'Next Appointment' },
        { field: 'metadata.contracted_measure_name', label: 'Measure Name' },
        { field: 'metadata.pharmacy', label: 'Pharmacy' }
      ],
      measureNameOptions: [
        { id: 'Adolescent Immunization (Combo 2)', name: 'Adolescent Immunization (Combo 2)' },
        { id: 'All Cause Readmissions', name: 'All Cause Readmissions' },
        { id: 'Annual Planned Visit', name: 'Annual Planned Visit' },
        { id: 'Appropriate Treatment for Children With Upper Respiratory Infection', name: 'Appropriate Treatment for Children With Upper Respiratory Infection' },
        { id: 'Asthma Medication Ratio', name: 'Asthma Medication Ratio' },
        { id: 'Avoidance of Antibiotic Treatment for Acute Bronchitis - Adult', name: 'Avoidance of Antibiotic Treatment for Acute Bronchitis - Adult' },
        { id: 'Breast Cancer Screening', name: 'Breast Cancer Screening' },
        { id: 'COA: Functional Status Assessment', name: 'COA: Functional Status Assessment' },
        { id: 'Cervical Cancer Screening', name: 'Cervical Cancer Screening' },
        { id: 'Child and Adolecent Well Care Visits', name: 'Child and Adolecent Well Care Visits' },
        { id: 'Child and Adolescent Well Care Visits ', name: 'Child and Adolescent Well Care Visits ' },
        { id: 'Child and Adolescent Well Care Visits - Age 12-17', name: 'Child and Adolescent Well Care Visits - Age 12-17' },
        { id: 'Child and Adolescent Well Care Visits - Age 3-11', name: 'Child and Adolescent Well Care Visits - Age 3-11' },
        { id: 'Childhood Immunization Status - Combo 3', name: 'Childhood Immunization Status - Combo 3' },
        { id: 'Childhood Immunization Status - MMR', name: 'Childhood Immunization Status - MMR' },
        { id: 'Chlamydia Screening', name: 'Chlamydia Screening' },
        { id: 'Colorectal Cancer Screening', name: 'Colorectal Cancer Screening' },
        { id: 'Concurrent use of Opioids and Benzadiazepines', name: 'Concurrent use of Opioids and Benzadiazepines' },
        { id: 'Controlling Blood Pressure', name: 'Controlling Blood Pressure' },
        { id: 'Depression Screening', name: 'Depression Screening' },
        { id: 'Diabetes Care - Controlling Blood Pressure', name: 'Diabetes Care - Controlling Blood Pressure' },
        { id: 'Diabetes Care - HbA1c Control (<8%)', name: 'Diabetes Care - HbA1c Control (<8%)' },
        { id: 'Diabetes Care - HbA1c Control (<=9%)', name: 'Diabetes Care - HbA1c Control (<=9%)' },
        { id: 'Diabetes Care - HbA1c Poor Control (>9%)', name: 'Diabetes Care - HbA1c Poor Control (>9%)' },
        { id: 'Diabetes Care - Kidney Health Evaluation (albumin-creatinine)', name: 'Diabetes Care - Kidney Health Evaluation (albumin-creatinine)' },
        { id: 'Diabetes Care - Kidney Health Evaluation (glomerular filtration rate)', name: 'Diabetes Care - Kidney Health Evaluation (glomerular filtration rate)' },
        { id: 'Diabetes Care - Kidney Health Evaluation', name: 'Diabetes Care - Kidney Health Evaluation' },
        { id: 'Eye Exam for Patients With Diabetes', name: 'Eye Exam for Patients With Diabetes' },
        { id: 'Follow-Up After ED Visit for Mental Illness - 7 days', name: 'Follow-Up After ED Visit for Mental Illness - 7 days' },
        { id: 'Follow-Up after ED Visit', name: 'Follow-Up after ED Visit' },
        { id: 'Glycemic Status Patients With Diabetes', name: 'Glycemic Status Patients With Diabetes' },
        { id: 'Initiation of Pharmacotherapy upon New Episode of Opiod Dependence', name: 'Initiation of Pharmacotherapy upon New Episode of Opiod Dependence' },
        { id: 'Medication Adherence - Cholesterol', name: 'Medication Adherence - Cholesterol' },
        { id: 'Medication Adherence - Diabetes', name: 'Medication Adherence - Diabetes' },
        { id: 'Medication Adherence - Hypertension', name: 'Medication Adherence - Hypertension' },
        { id: 'Osteoporosis Management in Women Who Had a Fracture', name: 'Osteoporosis Management in Women Who Had a Fracture' },
        { id: 'POLY-ACH', name: 'POLY-ACH' },
        { id: 'Pharmacotherapy for Opioid Use Disorder', name: 'Pharmacotherapy for Opioid Use Disorder' },
        { id: 'Postpartum Care', name: 'Postpartum Care' },
        { id: 'Statin Therapy - Patients with Cardiovascular Disease', name: 'Statin Therapy - Patients with Cardiovascular Disease' },
        { id: 'Statin Use in Person with Diabetes ', name: 'Statin Use in Person with Diabetes ' },
        { id: 'Statin Use in Person with Diabetes', name: 'Statin Use in Person with Diabetes' },
        { id: 'Timeliness of Prenatal Care', name: 'Timeliness of Prenatal Care' },
        { id: 'Use of Imaging Studies for Low Back Pain', name: 'Use of Imaging Studies for Low Back Pain' },
        { id: 'Use of Opioids at High Dosage', name: 'Use of Opioids at High Dosage' },
        { id: 'Weight Assessment and Counseling for Physical Activity for Children/Adolescents ', name: 'Weight Assessment and Counseling for Physical Activity for Children/Adolescents ' },
        { id: 'Well Child Visits 0-30 Months', name: 'Well Child Visits 0-30 Months' }
      ],
      dropdownStyle: {
        position: 'fixed',
        top: '0px',
        left: '0px',
        zIndex: 9999,
        minWidth: '250px',
        maxWidth: '350px'
      }
    };
  },
  computed: {
    hasActiveFilters() {
      return this.filters.contractedMeasureName.length > 0 || this.filters.pharmacy;
    }
  },
  methods: {
    updateDropdownPosition() {
      const headerEl = this.$el.querySelector('.flex');
      if (!headerEl) return;

      const rect = headerEl.getBoundingClientRect();

      this.dropdownStyle = {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        zIndex: 9999,
        minWidth: '250px',
        maxWidth: '350px'
      };
    },

    toggleSortMenu() {
      this.showSortMenu = !this.showSortMenu;
      this.showFilterMenu = false;
      if (this.showSortMenu) {
        this.$nextTick(() => {
          this.updateDropdownPosition();
        });
      }
    },

    toggleFilterMenu() {
      this.showFilterMenu = !this.showFilterMenu;
      this.showSortMenu = false;
      if (this.showFilterMenu) {
        this.$nextTick(() => {
          this.updateDropdownPosition();
        });
      }
    },

    applySort(option) {
      const newDirection =
        this.currentSort?.field === option.field && this.currentSort?.direction === 'asc'
          ? 'desc'
          : 'asc';

      this.currentSort = {
        field: option.field,
        direction: newDirection
      };

      this.params.context.onExtraInfoSort({
        field: option.field,
        direction: newDirection
      });

      this.showSortMenu = false;
    },

    clearSort() {
      this.currentSort = null;
      this.params.context.onExtraInfoSort(null);
      this.showSortMenu = false;
    },

    applyFilters() {
      this.params.context.onExtraInfoFilter({
        contractedMeasureName: this.filters.contractedMeasureName,
        pharmacy: this.filters.pharmacy
      });
    },

    clearFilters() {
      this.filters = {
        contractedMeasureName: [],
        pharmacy: ''
      };

      this.params.context.onExtraInfoClearFilters();
      this.showFilterMenu = false;
    }
  },
  mounted() {
    const handleClickOutside = (e) => {
      const sortMenu = this.$refs.sortMenu;
      const filterMenu = this.$refs.filterMenu;
      const header = this.$el;

      // Check if click is on a teleported StandardMultipleDropdown or its panel
      const clickedElement = e.target;
      const isDropdownClick = clickedElement.closest('.relative') ||
                              clickedElement.closest('[style*="position: fixed"]');

      if (!header.contains(e.target) &&
          (!sortMenu || !sortMenu.contains(e.target)) &&
          (!filterMenu || !filterMenu.contains(e.target)) &&
          !isDropdownClick) {
        this.showSortMenu = false;
        this.showFilterMenu = false;
      }
};

    document.addEventListener('click', handleClickOutside);

    const handleScroll = () => {
      if (this.showSortMenu || this.showFilterMenu) {
        this.updateDropdownPosition();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    this.cleanupFunctions = [
      () => document.removeEventListener('click', handleClickOutside),
      () => window.removeEventListener('scroll', handleScroll, true),
      () => window.removeEventListener('resize', handleScroll)
    ];
  },

  beforeUnmount() {
    if (this.cleanupFunctions) {
      this.cleanupFunctions.forEach(fn => fn());
    }
  }
};
</script>

<style>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out;
}
</style>
